import React, { useState, useEffect } from 'react';
import { getCustomers, createCustomer, updateCustomer, deleteCustomer } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Users, Plus, Search, Mail, Phone, MapPin, 
  Loader2, Edit2, Trash2, X, Save
} from 'lucide-react';
import { toast } from 'sonner';

export default function AdminCustomers() {
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  // Form State
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', address: '', city: '', tax_id: '', notes: ''
  });

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    setIsLoading(true);
    try {
      const data = await getCustomers();
      setCustomers(data);
    } catch {
      toast.error('Kunden konnten nicht geladen werden');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (cust) => {
    setEditingId(cust.id);
    setFormData({ ...cust });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Kunden wirklich löschen?')) return;
    try {
      await deleteCustomer(id);
      toast.success('Kunde gelöscht');
      loadCustomers();
    } catch {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await updateCustomer(editingId, formData);
        toast.success('Kunde aktualisiert');
      } else {
        await createCustomer(formData);
        toast.success('Kunde erstellt');
      }
      setShowModal(false);
      resetForm();
      loadCustomers();
    } catch {
      toast.error('Fehler beim Speichern');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', phone: '', address: '', city: '', tax_id: '', notes: '' });
    setEditingId(null);
  };

  const filtered = customers.filter(c => 
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    c.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1a2f38]">Kundenverwaltung</h1>
          <p className="text-slate-500">Kundenstamm und CRM</p>
        </div>
        <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-[#1a2f38] hover:bg-[#2a4048]">
          <Plus className="w-4 h-4 mr-2" />
          Neuer Kunde
        </Button>
      </div>

      <Card className="shadow-sm">
        <CardHeader className="pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input 
              placeholder="Suche nach Name oder E-Mail..." 
              className="pl-10" 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center p-8"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-20" />
              <p>Keine Kunden gefunden</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map(cust => (
                <div key={cust.id} className="p-4 rounded-xl border border-slate-100 bg-white hover:border-[#1a2f38]/30 transition-colors shadow-sm relative group">
                  <div className="flex justify-between items-start mb-3">
                    <div className="w-10 h-10 rounded-full bg-[#1a2f38]/5 flex items-center justify-center text-[#1a2f38] font-bold">
                      {cust.name.charAt(0)}
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => handleEdit(cust)} className="p-1.5 hover:bg-slate-100 rounded text-slate-600"><Edit2 className="w-3.5 h-3.5" /></button>
                      <button onClick={() => handleDelete(cust.id)} className="p-1.5 hover:bg-red-50 rounded text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                  <h3 className="font-bold text-slate-800">{cust.name}</h3>
                  <div className="mt-3 space-y-1.5 text-sm text-slate-500">
                    <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5" />{cust.email || '—'}</div>
                    <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5" />{cust.phone || '—'}</div>
                    <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5" />{cust.city || '—'}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg shadow-2xl">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{editingId ? 'Kunde bearbeiten' : 'Neuer Kunde'}</CardTitle>
              <button onClick={() => setShowModal(false)}><X className="w-5 h-5 text-slate-400" /></button>
            </CardHeader>
            <form onSubmit={handleSubmit}>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-1.5">
                    <Label>Name / Firma</Label>
                    <Input required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>E-Mail</Label>
                    <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Telefon</Label>
                    <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} />
                  </div>
                  <div className="col-span-2 space-y-1.5">
                    <Label>Adresse</Label>
                    <Input value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Stadt / PLZ</Label>
                    <Input value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>UID-Nummer</Label>
                    <Input value={formData.tax_id} onChange={(e) => setFormData({...formData, tax_id: e.target.value})} />
                  </div>
                  <div className="col-span-2 space-y-1.5">
                    <Label>Notizen</Label>
                    <Input value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} />
                  </div>
                </div>
              </CardContent>
              <div className="p-4 border-t flex justify-end gap-3 bg-slate-50 rounded-b-xl">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Abbrechen</Button>
                <Button type="submit" className="bg-[#1a2f38] hover:bg-[#2a4048]">
                  <Save className="w-4 h-4 mr-2" />
                  Speichern
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
}
